import { minus_circle_disabled, minus_circle_enabled, plus_circle_disabled, plus_circle_enabled } from '@assets/icons';
import { ChipXButton, Text, Tooltip } from '..';
import { styleConstant } from '@constant';
import { Button, Popover } from '@mui/material';
import { colorSet } from '@theme';
import { useEffect, useRef, useState } from 'react';

interface IAdminCharacterNumberInputProps {
  disabled?: boolean;
  needTooltip?: boolean;
  tooltipText?: string;
  characterNumber: {
    maleNumber: number | string;
    femaleNumber: number | string;
  } | null;
  onApplyClick: (values: { maleNumber: number | string; femaleNumber: number | string }) => void;
  onChipXButtonClick?: () => void;
  isError?: boolean;
}

// CharacterNumberInput: 검색시 이용하는 인원수 인풋 / 남녀 합 3 초과 불가
// AdminCharacterNumberInput: 이미지 업로드/정보수정시 이용하는 인원수 인풋 / 남녀 합 제한 없음
export const AdminCharacterNumberInput = (props: IAdminCharacterNumberInputProps) => {
  const {
    disabled = false,
    needTooltip = false,
    tooltipText = '',
    characterNumber,
    onApplyClick,
    onChipXButtonClick,
    isError = false
  } = props;

  const [isSelectPaperOpen, setIsSelectPaperOpen] = useState<boolean>(false);
  const [displayText, setDisplayText] = useState<string | null>(
    !characterNumber ? null : `남자 ${characterNumber.maleNumber}명 ∙ 여자 ${characterNumber.femaleNumber}명`
  );
  const [initialCharacterNumber, setInitialCharacterNumber] = useState<{
    maleNumber: number | string;
    femaleNumber: number | string;
  }>({
    maleNumber: characterNumber?.maleNumber ?? 0,
    femaleNumber: characterNumber?.femaleNumber ?? 0
  });
  const [tempCharacterNumber, setTempCharacterNumber] = useState<{
    maleNumber: number | string;
    femaleNumber: number | string;
  }>({
    maleNumber: characterNumber?.maleNumber ?? 0,
    femaleNumber: characterNumber?.femaleNumber ?? 0
  });

  const options = [0, 1, 2, 3, '4+'];
  const selectRef = useRef<HTMLDivElement>(null);

  const handleNumberChange = (
    type: 'male' | 'female',
    action: 'increase' | 'decrease',
    options: (number | string)[]
  ) => {
    const currentValue =
      type === 'male' ? (tempCharacterNumber?.maleNumber ?? 0) : (tempCharacterNumber?.femaleNumber ?? 0);

    const currentIndex = options.indexOf(currentValue ?? 0);
    if (action === 'increase' && currentIndex < options.length - 1) {
      const newValue = options[currentIndex + 1];
      setTempCharacterNumber((prev) => ({
        ...prev,
        [type === 'male' ? 'maleNumber' : 'femaleNumber']: newValue
      }));
    } else if (action === 'decrease' && currentIndex > 0) {
      const newValue = options[currentIndex - 1];
      setTempCharacterNumber((prev) => ({
        ...prev,
        [type === 'male' ? 'maleNumber' : 'femaleNumber']: newValue
      }));
    }
  };

  const isApplyEnabled = () => {
    const hasChanged =
      tempCharacterNumber?.maleNumber !== initialCharacterNumber.maleNumber ||
      tempCharacterNumber?.femaleNumber !== initialCharacterNumber.femaleNumber;
    const hasValidSum =
      tempCharacterNumber?.maleNumber === '4+' ||
      tempCharacterNumber?.femaleNumber === '4+' ||
      (typeof tempCharacterNumber?.maleNumber === 'number' &&
        typeof tempCharacterNumber?.femaleNumber === 'number' &&
        tempCharacterNumber?.maleNumber + tempCharacterNumber?.femaleNumber >= 1);

    return hasChanged && hasValidSum;
  };

  const handleApplyClick = () => {
    const newText = `남자 ${tempCharacterNumber?.maleNumber}명 ∙ 여자 ${tempCharacterNumber?.femaleNumber}명`;
    setDisplayText(newText);
    setIsSelectPaperOpen(false);

    setInitialCharacterNumber({
      maleNumber: tempCharacterNumber?.maleNumber ?? 0,
      femaleNumber: tempCharacterNumber?.femaleNumber ?? 0
    });

    // 적용 버튼 클릭 시 현재 값들을 상위 컴포넌트로 전달
    onApplyClick({
      maleNumber: tempCharacterNumber?.maleNumber ?? 0,
      femaleNumber: tempCharacterNumber?.femaleNumber ?? 0
    });
  };

  const handleChipXButtonClick = () => {
    setDisplayText(null);
    setIsSelectPaperOpen(false);

    setInitialCharacterNumber({
      maleNumber: characterNumber?.maleNumber ?? 0,
      femaleNumber: characterNumber?.femaleNumber ?? 0
    });

    setTempCharacterNumber({
      maleNumber: characterNumber?.maleNumber ?? 0,
      femaleNumber: characterNumber?.femaleNumber ?? 0
    });

    if (onChipXButtonClick) {
      onChipXButtonClick();
    }
  };

  const NumberControl = ({
    value,
    options,
    onIncrease,
    onDecrease
  }: {
    value: number | string;
    options: (number | string)[];
    onIncrease: () => void;
    onDecrease: () => void;
  }) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        backgroundColor: colorSet.gray100,
        borderRadius: 20,
        padding: '4px'
      }}
    >
      <img
        onClick={onDecrease}
        src={options.indexOf(value) === 0 ? minus_circle_disabled : minus_circle_enabled}
        alt=""
        style={{ width: 32, height: 32, cursor: options.indexOf(value) === 0 ? 'default' : 'pointer' }}
      />

      <span style={{ width: '40px', textAlign: 'center' }}>
        <Text variant="subtitle" fontWeight="bold" color={disabled ? colorSet.gray300 : colorSet.gray600}>
          {value}
        </Text>
      </span>

      <img
        onClick={onIncrease}
        src={options.indexOf(value) === options.length - 1 ? plus_circle_disabled : plus_circle_enabled}
        alt=""
        style={{
          width: 32,
          height: 32,
          cursor: options.indexOf(value) === options.length - 1 ? 'default' : 'pointer'
        }}
      />
    </div>
  );

  // 기존 값으로 돌아가기 (적용 전 Paper 닫을 시 실행)
  const resetToLastSavedValues = () => {
    setTempCharacterNumber({
      maleNumber: initialCharacterNumber.maleNumber,
      femaleNumber: initialCharacterNumber.femaleNumber
    });
  };

  // Paper 닫기
  const handleClose = () => {
    setIsSelectPaperOpen(false);
    resetToLastSavedValues();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const selectPaper = document.querySelector('.selectPaper');
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node) &&
        selectPaper &&
        !selectPaper.contains(event.target as Node)
      ) {
        setIsSelectPaperOpen(false);
        resetToLastSavedValues();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [initialCharacterNumber]);

  // characterNumber prop value가 업데이트될 시 displayText, initialCharacterNumber, tempCharacterNumber setState
  useEffect(() => {
    if (!characterNumber) {
      setDisplayText(null);
      setInitialCharacterNumber({
        maleNumber: 0,
        femaleNumber: 0
      });
      setTempCharacterNumber({
        maleNumber: 0,
        femaleNumber: 0
      });
    } else {
      setDisplayText(`남자 ${characterNumber.maleNumber}명 ∙ 여자 ${characterNumber.femaleNumber}명`);
      setInitialCharacterNumber({
        maleNumber: characterNumber.maleNumber ?? 0,
        femaleNumber: characterNumber.femaleNumber ?? 0
      });
      setTempCharacterNumber({
        maleNumber: characterNumber.maleNumber ?? 0,
        femaleNumber: characterNumber.femaleNumber ?? 0
      });
    }
  }, [characterNumber]);

  return (
    <>
      <div className="characterNumberSelect" style={{ display: 'inline-flex', position: 'relative' }} ref={selectRef}>
        <div style={{ position: 'relative', display: 'inline-flex', whiteSpace: 'nowrap' }}>
          {!displayText ? (
            <div
              style={{
                height: 38,
                padding: '9px 16px',
                display: 'inline-flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '12px',
                border: `1px solid ${isSelectPaperOpen ? colorSet.primaryMain : isError ? colorSet.errorMain : colorSet.gray300}`,
                backgroundColor: disabled
                  ? colorSet.gray200
                  : isSelectPaperOpen
                    ? 'rgba(119, 78, 255, 0.15)'
                    : 'transparent',
                cursor: disabled ? 'default' : 'pointer'
              }}
              onClick={() => !disabled && setIsSelectPaperOpen(!isSelectPaperOpen)}
            >
              {needTooltip && <Tooltip text={tooltipText} />}
              <Text variant="body1">인원수</Text>
            </div>
          ) : (
            <div
              style={{
                height: 38,
                padding: '9px 16px',
                display: 'inline-flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '12px',
                border: `1px solid ${isSelectPaperOpen ? colorSet.primaryMain : colorSet.gray600}`,
                backgroundColor: isSelectPaperOpen ? 'rgba(119, 78, 255, 0.15)' : colorSet.gray200,
                cursor: 'pointer'
              }}
              onClick={() => !disabled && setIsSelectPaperOpen(!isSelectPaperOpen)}
            >
              {needTooltip && <Tooltip text={tooltipText} />}
              <Text variant="body1" fontWeight="bold" style={{ marginRight: 8 }}>
                인원수
              </Text>
              {displayText ? <Text variant="body1">{displayText}</Text> : null}
              <ChipXButton onClick={handleChipXButtonClick} />
            </div>
          )}
        </div>

        <Popover
          open={isSelectPaperOpen && !disabled}
          anchorEl={selectRef.current}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: {
              onMouseDown: (event) => {
                event.stopPropagation();
              },
              sx: {
                marginTop: '10px',
                width: '274px',
                padding: '12px 16px',
                backgroundColor: colorSet.white,
                boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                zIndex: styleConstant.zIndexChipPaper,
                maxWidth: 'calc(100vw - 16px)',
                maxHeight: 'calc(100vh - 16px)'
              }
            }
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              marginBottom: '12px'
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ width: 100, display: 'flex', justifyContent: 'center' }}>
                  <Text variant="body1">남자</Text>
                </div>
                <NumberControl
                  value={tempCharacterNumber?.maleNumber ?? 0}
                  options={options}
                  onIncrease={() => handleNumberChange('male', 'increase', options)}
                  onDecrease={() => handleNumberChange('male', 'decrease', options)}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ width: 100, display: 'flex', justifyContent: 'center' }}>
                  <Text variant="body1">여자</Text>
                </div>
                <NumberControl
                  value={tempCharacterNumber?.femaleNumber ?? 0}
                  options={options}
                  onIncrease={() => handleNumberChange('female', 'increase', options)}
                  onDecrease={() => handleNumberChange('female', 'decrease', options)}
                />
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              disabled={!isApplyEnabled()}
              variant="contained"
              onClick={() => {
                setIsSelectPaperOpen(false);
                handleApplyClick();
              }}
              sx={{ width: 90, height: 44, borderRadius: 22 }}
            >
              적용
            </Button>
          </div>
        </Popover>
      </div>
    </>
  );
};
